import React               from 'react';
import PropTypes           from 'prop-types';
import SearchResultsHeader from "./SearchResultsHeader";
import SearchResultsPage   from "./SearchResultsPage";
import Flux                from "../../flux/Flux";
import {setUrlParameters}  from "../../utils/CommonUtils";
import PjaxWrapper         from "../../utils/PjaxWrapper";

class SearchResults extends React.PureComponent {

	constructor(props) {
		super(props);

		this.state = {
			actorResultCount: (this.props.resultCounts.actors),
			videoResultCount: (this.props.resultCounts.videos),
			tvResultCount:    (this.props.resultCounts.tv),
			blogResultCount:  (this.props.resultCounts.blog),
			result:           null,
			forceVxLoader:    false,
			pagination:       {
				total:       0,
				currentPage: 1,
				maxPage:     1,
			},
			filter:           null,
		};

		this.onSearchResult      = this.onSearchResult.bind(this);
		this.updateResultCount   = this.updateResultCount.bind(this);
		this.setActiveCategory   = this.setActiveCategory.bind(this);
		this.getFilterConfig     = this.getFilterConfig.bind(this);
		this.getDefaultFilter    = this.getDefaultFilter.bind(this);
		this.getCurrentOffset    = this.getCurrentOffset.bind(this);
		this.getItemsPerPage     = this.getItemsPerPage.bind(this);
		this.doSearch            = this.doSearch.bind(this);
		this.onCategoryChange    = this.onCategoryChange.bind(this);
		this.onFilterChange      = this.onFilterChange.bind(this);
		this.onPaginationChange  = this.onPaginationChange.bind(this);
		this.resetFilter         = this.resetFilter.bind(this);
		this.pushHistory         = this.pushHistory.bind(this);
		this.onBrowserNavigation = this.onBrowserNavigation.bind(this);
		this.onBeforePjaxReload  = this.onBeforePjaxReload.bind(this);

		this.baseUrl = setUrlParameters(window.location.href, {});
		this.setActiveCategory(this.props.category);

		const lastRequest = Flux.Search.getLastRequest();
		if (lastRequest && lastRequest.searchPattern === this.props.searchPattern && this.state.activeCategory === lastRequest.category) {
			this.state.pagination.currentPage = Math.ceil((lastRequest.offset + 1) / this.getItemsPerPage());
			this.state.filter                 = lastRequest.filter;
		} else {
			this.state.filter = this.getDefaultFilter(this.state.activeCategory);
		}
	}

	componentDidMount() {
		Flux.Browser.addWindowPopStateListener(this.onBrowserNavigation);
		PjaxWrapper.addOnBeforeSendCallback(this.onBeforePjaxReload);
		Flux.Search.addSearchResultListener(this.onSearchResult);
		Flux.Search.setCurrentSearchPattern(this.props.searchPattern);
		this.doSearch(this.props.searchPattern);

		const url          = window.location.href;
		const historyState = {
			...window.history && window.history.state || {},
			activeCategory: this.state.activeCategory,
			currentPage:    this.state.pagination.currentPage,
			url,
			filter:         this.state.filter,
		};
		Flux.Browser.historyReplaceState(historyState, '', url);
	}

	componentWillUnmount() {
		Flux.Browser.removeWindowPopStateListener(this.onBrowserNavigation);
		PjaxWrapper.removeOnBeforeSendCallback(this.onBeforePjaxReload);
		Flux.Search.removeSearchResultListener(this.onSearchResult);
		Flux.Search.setCurrentSearchPattern(null);
	}

	onBeforePjaxReload(opts) {
		//check if still same page after reload (opts.url could also be relative path - modify for comparison)
		const toSearchResultBlock = typeof opts.url === 'string' && setUrlParameters(opts.url.includes(window.location.origin) ? opts.url : window.location.origin + opts.url, {}) === this.baseUrl;

		//suppress pjax reload when false
		return !toSearchResultBlock;
	}

	parseResultCount(count) {
		return count > 9999 ? 9999 : count;
	}

	onSearchResult(result) {
		this.state.result             = result;
		this.state.pagination.total   = this.parseResultCount(result.total);
		this.state.pagination.maxPage = Math.ceil(this.state.pagination.total / this.getItemsPerPage());
		this.state.forceVxLoader      = false;

		this.updateResultCount(result.total);

		this.forceUpdate();
	}

	updateResultCount(count) {
		switch (this.state.activeCategory) {
			case Flux.Constants.SearchResults.Categories.actors.name:
				this.state.actorResultCount = (count);
				break;
			case Flux.Constants.SearchResults.Categories.videos.name:
				this.state.videoResultCount = (count);
				break;
			case Flux.Constants.SearchResults.Categories.tv.name:
				this.state.tvResultCount = (count);
				break;
			case Flux.Constants.SearchResults.Categories.blog.name:
				this.state.blogResultCount = (count);
				break;
			default:
				break;
		}
	}

	setActiveCategory(category) {
		let count = 0;
		switch (category) {
			case Flux.Constants.SearchResults.Categories.actors.name:
				count = this.state.actorResultCount;
				break;
			case Flux.Constants.SearchResults.Categories.videos.name:
				count = this.state.videoResultCount;
				break;
			case Flux.Constants.SearchResults.Categories.tv.name:
				count = this.state.tvResultCount;
				break;
			case Flux.Constants.SearchResults.Categories.blog.name:
				count = this.state.blogResultCount;
				break;
			default:
				break;
		}

		if (count > 0) {
			this.state.activeCategory = category;

			return true;
		}

		if (category === this.props.category) {
			for (const cat in Flux.Constants.SearchResults.Categories) {
				if (cat === category) {
					continue;
				}

				if (this.setActiveCategory(cat)) {
					return true;
				}
			}

			// all counts are 0
			this.state.activeCategory = category;
		}

		return false;
	}

	getFilterConfig() {
		return this.props.filterConfigs[this.state.activeCategory];
	}

	getDefaultFilter(category) {
		return JSON.parse(JSON.stringify(this.props.defaultFilters[category]));
	}

	getItemsPerPage() {
		let itemsPerPage = 0;

		switch (this.state.activeCategory) {
			case Flux.Constants.SearchResults.Categories.blog.name:
				itemsPerPage = Flux.Constants.SearchResults.Categories.blog.itemsPerPage;
				break;
			default:
				itemsPerPage = 30;
		}

		const wSize = Flux.Browser.getWindowSize();
		if (wSize && wSize.width && wSize.width < 1201) {
			let columns;
			if (wSize.width < 769) {
				// 2 columns
				columns = 2;
			} else if (wSize.width < 993) {
				// 3 columns
				columns = 3;
			} else {
				// 4 columns
				columns = 4;
			}

			itemsPerPage = itemsPerPage - (itemsPerPage % columns);
		}

		return itemsPerPage;
	}

	getCurrentOffset() {
		return this.getItemsPerPage() * (this.state.pagination.currentPage - 1);
	}

	doSearch(searchPattern) {
		Flux.Search.doSearch(searchPattern, this.state.activeCategory, this.state.filter, this.getCurrentOffset(), this.getItemsPerPage(), true);
	}

	onCategoryChange(activeCategory, currentPage = 1, pushState = true, filter = this.getDefaultFilter(activeCategory)) {
		if (activeCategory !== this.state.activeCategory) {
			if (pushState) {
				this.pushHistory(activeCategory, currentPage, '', filter);
			}

			this.setState({
				activeCategory,
				filter,
				result:           null,
				pagination:       {currentPage},
				actorResultCount: this.props.resultCounts.actors,
				videoResultCount: this.props.resultCounts.videos,
				tvResultCount:    this.props.resultCounts.tv,
				blogResultCount:  this.props.resultCounts.blog,
			}, () => this.doSearch(this.props.searchPattern));
		}
	}

	onFilterChange(filter) {
		this.pushHistory('', 1, window.location.href, filter);
		this.setState({
			filter:        filter,
			forceVxLoader: true,
			pagination:    {currentPage: 1, maxPage: 1, total: 0},
		}, () => this.doSearch(this.props.searchPattern));
	}

	onPaginationChange(newPage) {
		this.pushHistory('', newPage, window.location.href, this.state.filter);
		this.setState({result: null, pagination: {currentPage: newPage}}, () => this.doSearch(this.props.searchPattern));
	}

	resetFilter() {
		this.setState({
			filter:           this.getDefaultFilter(this.state.activeCategory),
			pagination:       {currentPage: 1},
			actorResultCount: (this.props.resultCounts.actors),
			videoResultCount: (this.props.resultCounts.videos),
			tvResultCount:    (this.props.resultCounts.tv),
			blogResultCount:  (this.props.resultCounts.blog),
		}, () => this.doSearch(this.props.searchPattern));
	}

	pushHistory(newCategory = '', newPage = 1, currentUrl = '', filter) {
		const url          = currentUrl ? currentUrl : setUrlParameters(window.location.href, {category: newCategory});
		const historyState = {
			...window.history && window.history.state || {},
			activeCategory: newCategory !== '' ? newCategory : this.state.activeCategory,
			currentPage:    newPage,
			url,
			filter,
		};
		Flux.Browser.historyPushState(historyState, '', url);
	}

	onBrowserNavigation(event) {
		const {activeCategory, currentPage, filter} = event.state;

		if(activeCategory === this.state.activeCategory) {
			this.setState({
				pagination:       {
					currentPage: currentPage,
				},
				result:           null,
				actorResultCount: (this.props.resultCounts.actors),
				videoResultCount: (this.props.resultCounts.videos),
				tvResultCount:    (this.props.resultCounts.tv),
				blogResultCount:  (this.props.resultCounts.blog),
				filter,
			}, () => this.doSearch(this.props.searchPattern));
		} else {
			this.onCategoryChange(activeCategory, currentPage, false, filter);
		}
	}

	render() {
		return (
			<div>
				<SearchResultsHeader activeCategory={this.state.activeCategory}
				                     actorResultCount={this.state.actorResultCount}
				                     videoResultCount={this.state.videoResultCount}
				                     tvResultCount={this.state.tvResultCount}
				                     blogResultCount={this.state.blogResultCount}
				                     onCategoryChange={this.onCategoryChange}
				/>
				<SearchResultsPage filterConfig={this.getFilterConfig()}
				                   filter={this.state.filter}
				                   onFilterChange={this.onFilterChange}
				                   onPaginationChange={this.onPaginationChange}
				                   pagination={this.state.pagination}
				                   result={this.state.result}
				                   onResetFilter={this.resetFilter}
				                   forceVxLoader={this.state.forceVxLoader}
				/>
			</div>
		);
	}
}

SearchResults.propTypes = {
	searchPattern:  PropTypes.string,
	category:       PropTypes.string,
	resultCounts:   PropTypes.shape({
		actors: PropTypes.number,
		videos: PropTypes.number,
		tv:     PropTypes.number,
		blog:   PropTypes.number,
	}),
	filterConfigs:  PropTypes.shape({
		gender:   PropTypes.arrayOf(PropTypes.string),
		online:   PropTypes.bool,
		new:      PropTypes.bool,
		sortings: PropTypes.arrayOf(PropTypes.string),
	}).isRequired,
	defaultFilters: PropTypes.shape({
		actors: PropTypes.object,
		videos: PropTypes.object,
		tv:     PropTypes.object,
		blog:   PropTypes.object,
	}).isRequired,
};

SearchResults.defaultProps = {
	category: Flux.Constants.SearchResults.Categories.actors.name,
};

export default SearchResults;
